/**
 * Copyright © 2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box, Card, CardContent, Typography, TextField, Button,
    InputAdornment, IconButton, Alert, CircularProgress,
} from '@mui/material';
import { Lock, Visibility, VisibilityOff, CheckCircleOutline } from '@mui/icons-material';
import { navLogoUrl } from './brandAssets';

const BASE_URL = process.env.REACT_APP_CONNECTOR_API_URL;

function ResetPasswordPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const [form, setForm] = useState({ newPassword: '', confirmPassword: '' });
    const [errors, setErrors] = useState({});
    const [show, setShow] = useState({ new: false, confirm: false });
    const [submitting, setSubmitting] = useState(false);
    const [apiError, setApiError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(f => ({ ...f, [name]: value }));
        if (errors[name]) setErrors(err => ({ ...err, [name]: '' }));
        setApiError('');
    };

    const validate = () => {
        const e = {};
        if (!form.newPassword) e.newPassword = 'New password is required.';
        else if (form.newPassword.length < 8) e.newPassword = 'Must be at least 8 characters.';
        if (form.newPassword !== form.confirmPassword) e.confirmPassword = 'Passwords do not match.';
        return e;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }

        setSubmitting(true);
        setApiError('');

        try {
            const res = await fetch(`${BASE_URL}/api/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, newPassword: form.newPassword }),
            });

            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                setApiError(data.error || 'Failed to reset password. Please try again.');
                return;
            }

            setSuccess(true);
        } catch {
            setApiError('Unable to reach the server. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const passwordField = (label, name, showKey) => (
        <TextField
            fullWidth
            label={label}
            name={name}
            type={show[showKey] ? 'text' : 'password'}
            value={form[name]}
            onChange={handleChange}
            error={Boolean(errors[name])}
            helperText={errors[name]}
            margin="normal"
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <Lock sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                ),
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShow(s => ({ ...s, [showKey]: !s[showKey] }))} edge="end">
                            {show[showKey] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f0f4f3',
                px: 2,
                py: 6,
            }}
        >
            <Card
                elevation={0}
                sx={{
                    width: '100%',
                    maxWidth: 440,
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(24, 62, 61, 0.1)',
                    border: '1px solid rgba(24, 62, 61, 0.06)',
                }}
            >
                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Box component="img" src={navLogoUrl} alt="Zamp" sx={{ height: 28, width: 'auto' }} />
                    </Box>

                    <Typography
                        component="h1"
                        align="center"
                        sx={{
                            fontFamily: 'var(--zamp-font-headline) !important',
                            fontSize: '1.35rem',
                            fontWeight: 600,
                            color: '#1e293b',
                            mb: 1,
                        }}
                    >
                        Reset your password
                    </Typography>

                    {!success && (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
                            Choose a new password for your account.
                        </Typography>
                    )}

                    {!token && !success && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                            This password reset link is missing its token. Please use the link from your email, or request a new one from the login page.
                        </Alert>
                    )}

                    {success ? (
                        <>
                            <Alert
                                severity="success"
                                icon={<CheckCircleOutline fontSize="inherit" />}
                                sx={{ mb: 2, borderRadius: 2 }}
                            >
                                Your password has been reset successfully.
                            </Alert>
                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 1.2 }}
                                onClick={() => navigate('/login')}
                            >
                                Back to login
                            </Button>
                        </>
                    ) : (
                        <Box component="form" onSubmit={handleSubmit} noValidate>
                            {apiError && (
                                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setApiError('')}>
                                    {apiError}
                                </Alert>
                            )}

                            {passwordField('New Password', 'newPassword', 'new')}
                            {passwordField('Confirm New Password', 'confirmPassword', 'confirm')}

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={submitting || !token}
                                sx={{ mt: 3, borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 1.2 }}
                            >
                                {submitting ? <CircularProgress size={22} color="inherit" /> : 'Reset Password'}
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

export default ResetPasswordPage;
